import { useState, useCallback, useRef, useMemo } from 'react'
import {
  CLUSTER_FORM_MODE,
  VIEW_MODE_OPTIONS,
  STAT_TYPE_OPTIONS,
  BLOCK_TYPE_OPTIONS,
  STOP_WORD_LEVEL_OPTIONS,
  DEFAULT_STAT_TYPE,
  DEFAULT_BLOCK_TYPE,
  DEFAULT_STOP_WORD_LEVEL
} from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'
import {
  getOptionsList,
  getBatchesOptions,
  getBatchesValue,
  calcSelectedClustersStat
} from '@aytm/app/stats/page/question-views/hm/components/controls/lib/utils'
import { getRandomColor } from '@aytm/app/stats/page/question-views/hm/components/controls/cluster-form/lib/utils'
import { decodeText } from '@aytm/app/stats/lib/util/helpers'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

const getDefaultForm = ({
  clusters,
  components,
  viewMode,
  answer,
  config,
  batches,
  clustersMode,
  qLogicVisibility,
  loading,
  statType,
  blockType,
  stopWordLevel
}) => {
  const clustersOn = clusters?.filter(cluster => !cluster.off)

  return {
    isLoading: 'loading' === answer || loading || false,
    viewMode: {
      value: viewMode,
      isEnabled: isComponentOn(components, 'options_chart_view'),
      options: getOptionsList({
        viewModeOptions: VIEW_MODE_OPTIONS,
        clusters,
        viewMode
      })
    },
    filterPinTypes: {
      isEnabled: isComponentOn(components, 'options_chart_pins') && batches?.length,
      value: getBatchesValue({ config, batches }),
      options: getBatchesOptions({ batches, qLogicVisibility })
    },
    clusters: {
      isEnabled: isComponentOn(components, 'options_chart_clusters'),
      showControls: clusters?.length > 0,
      clustersMode: clustersMode !== 'clusters-off',
      maskToSelected: config?.applyClustersMask || false,
      value: clustersOn.map(cluster => cluster?.uid) || [],
      options:
        clusters?.map(cluster => {
          // the title is stored encoded and the list draws its options as text
          return { text: decodeText(cluster?.title, false), value: cluster?.uid }
        }) || [],
      stat: calcSelectedClustersStat(clustersOn || [], answer === 'loading' ? null : answer)
    },
    answersStats: {
      isEnabled: clusters?.length > 0,
      statType: {
        value: statType ?? DEFAULT_STAT_TYPE,
        options: STAT_TYPE_OPTIONS
      },
      blockType: {
        value: blockType ?? DEFAULT_BLOCK_TYPE,
        options: BLOCK_TYPE_OPTIONS,
        isVisible: (statType ?? DEFAULT_STAT_TYPE) !== 'pins'
      },
      stopWordLevel: {
        value: stopWordLevel ?? DEFAULT_STOP_WORD_LEVEL,
        options: STOP_WORD_LEVEL_OPTIONS,
        isVisible: (statType ?? DEFAULT_STAT_TYPE) !== 'pins' && (blockType ?? DEFAULT_BLOCK_TYPE) === 'word'
      }
    }
  }
}

export const useControlsForm = ({
  clusters,
  components,
  viewMode,
  answer,
  config,
  batches,
  qLogicVisibility,
  loading,
  clustersMode,
  statType,
  blockType,
  stopWordLevel
}) => {
  const dataForm = getDefaultForm({
    clusters,
    components,
    viewMode,
    answer,
    config,
    batches,
    qLogicVisibility,
    loading,
    clustersMode,
    statType,
    blockType,
    stopWordLevel
  })

  const [uiForm, setUiForm] = useState(CLUSTER_FORM_MODE.OFF)

  const [tip, setTip] = useState(1)
  const [editClusterId, setEditClusterId] = useState(null)
  const lassoDataRef = useRef(null)

  const currentCluster = useMemo(() => {
    if (uiForm === CLUSTER_FORM_MODE.ADD) {
      return {
        border: [],
        color: getRandomColor(),
        clusterType: 'custom',
        off: false,
        points: [],
        title: null,
        stat: { points: 0, comments: 0, respondents: 0 },
        uid: null
      }
    }

    if (uiForm === CLUSTER_FORM_MODE.EDIT) {
      return clusters?.find(c => c.uid === editClusterId) || null
    }

    return null
  }, [uiForm, clusters, editClusterId])

  const setLassoData = useCallback(data => {
    lassoDataRef.current = data
  }, [])

  const getLassoData = useCallback(() => lassoDataRef.current, [])

  const changeClustersForm = (mode, uid = null) => {
    const isEdit = mode === CLUSTER_FORM_MODE.EDIT

    setEditClusterId(isEdit ? uid : null)
    setTip(isEdit ? 3 : 1)

    lassoDataRef.current = null

    setUiForm(mode)
  }

  const form = {
    ...dataForm,
    clustersForm: { mode: uiForm }
  }

  return {
    form,
    changeClustersForm,
    editClusterId,
    currentCluster,
    tip,
    setTip,
    getLassoData,
    setLassoData
  }
}
